import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';

/**
 * useTierGating — Hook for plan-based feature gating.
 * Returns entitlements, feature check function, and loading state.
 */
export const useTierGating = () => {
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id || '';
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/pricing/entitlements`, {
        headers: { 'x-org-id': orgId },
      });
      if (res.ok) {
        setEntitlements(await res.json());
      }
    } catch (e) {
      console.warn('Tier gating fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { refresh(); }, [refresh]);

  const canAccess = useCallback(
    (feature) => {
      if (!entitlements) return true; // Allow while loading
      return entitlements.entitlements?.[feature] ?? false;
    },
    [entitlements]
  );

  const getPlan = useCallback(
    () => entitlements?.plan || 'basic',
    [entitlements]
  );

  const getUpgradePlan = useCallback(
    (feature) => {
      const featureMap = {
        voice_setup: 'Professional',
        telephony: 'Professional',
        orion: 'Professional',
        multi_user: 'Teams',
        multi_tenant: 'Partner Pro',
        advanced_menu: 'Partner Pro',
        byo_llm: 'Partner Pro',
      };
      return featureMap[feature] || 'Professional';
    },
    []
  );

  return {
    entitlements,
    loading,
    canAccess,
    getPlan,
    getUpgradePlan,
    refresh,
    plan: entitlements?.plan || 'basic',
    isBasic: (entitlements?.plan || 'basic') === 'basic',
    isPro: ['professional', 'teams', 'partner_pro'].includes(entitlements?.plan || 'basic'),
    isPartnerPro: (entitlements?.plan || 'basic') === 'partner_pro',
  };
};

export default useTierGating;
